import React, { useMemo } from "react";
import { Button, Stack } from "@mui/material";
import PrintIcon from "@mui/icons-material/Print";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

import { generateHTML } from "@tiptap/html";

import StarterKit from "@tiptap/starter-kit";
import Image from "@tiptap/extension-image";
import Underline from "@tiptap/extension-underline";
import TextAlign from "@tiptap/extension-text-align";

import { Table } from "@tiptap/extension-table";
import { TableRow } from "@tiptap/extension-table-row";
import { TableCell } from "@tiptap/extension-table-cell";
import { TableHeader } from "@tiptap/extension-table-header";

import "./questionPaper.css";
import Superscript from "@tiptap/extension-superscript";
import Subscript from "@tiptap/extension-subscript";
import Mathematics from "@tiptap/extension-mathematics";

const QuestionPaperPrint = ({
  paper,
  onBack,
  schoolName = localStorage.getItem("name"),
  schoolLogo = localStorage.getItem("logoUrl"),
}) => {

  /*
   * Tiptap extensions used for rendering saved JSON.
   */
  const extensions = useMemo(
    () => [
      StarterKit,
      Underline,
      Superscript,
      Subscript,
      Image,
      TextAlign.configure({
        types: ["heading", "paragraph"],
      }),
      Table.configure({
        resizable: false,
      }),
      TableRow,
      TableHeader,
      TableCell,
      Mathematics,
    ],
    []
  );

  /*
   * Convert Tiptap JSON into HTML.
   */
  const renderQuestion = (content) => {
    try {
      if (!content) {
        return "";
      }

      const jsonContent =
        typeof content === "string"
          ? JSON.parse(content)
          : content;

      return generateHTML(jsonContent, extensions);
    } catch (error) {
      console.error("Question render error:", error);
      return "";
    }
  };

  /*
   * Print
   */
  const handlePrint = () => {
    window.print();
  };

  /*
   * Calculate total marks from questions.
   * If paper.totalMarks is available, it is preferred.
   */
  const calculateMarks = () => {
    let total = 0;

    paper?.sections?.forEach((section) => {
      section?.questions?.forEach((question) => {
        total += Number(question?.marks || 0);
      });
    });

    return total;
  };

  const calculatedMarks = calculateMarks();

  /*
   * Global question number.
   *
   * Example:
   * Section A
   * Q1
   * Q2
   *
   * Section B
   * Q3
   * Q4
   */
  let questionNumber = 0;

  return (
    <div className="question-paper-container">

      {/* ===============================
          ACTION BUTTONS
      =============================== */}

      <div className="question-paper-actions">

        <Stack direction="row" spacing={1}>

          {onBack && (
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={onBack}
            >
              Back
            </Button>
          )}

          <Button
            variant="contained"
            startIcon={<PrintIcon />}
            onClick={handlePrint}
          >
            Print
          </Button>

        </Stack>

      </div>

      {/* ===============================
          PRINT AREA
      =============================== */}

      <div className="question-paper-print-area">

        <div className="question-paper-a4">

          {/* ===============================
              HEADER
          =============================== */}

          <div className="question-paper-header">

            {schoolLogo && (
              <img
                src={schoolLogo}
                alt="School Logo"
                className="question-paper-logo"
                onError={(e) => {
                  e.currentTarget.style.display = "none";
                }}
              />
            )}

            <h1 className="school-name">
              {schoolName}
            </h1>

            {paper?.examName && (
              <div className="exam-name">
                {paper.examName}
              </div>
            )}

            {paper?.title && (
              <div className="paper-title">
                {paper.title}
              </div>
            )}

          </div>

          {/* ===============================
              DETAILS
          =============================== */}

          <div className="exam-details">

            <div className="exam-detail-item">
              <span className="exam-detail-label">
                Class:
              </span>{" "}
              {paper?.className || "-"}
            </div>

            <div className="exam-detail-item">
              <span className="exam-detail-label">
                Subject:
              </span>{" "}
              {paper?.subject || "-"}
            </div>

            <div className="exam-detail-item">
              <span className="exam-detail-label">
                Academic Year:
              </span>{" "}
              {paper?.academicYear || "-"}
            </div>

            <div className="exam-detail-item">
              <span className="exam-detail-label">
                Duration:
              </span>{" "}
              {paper?.duration || "-"}
            </div>

            <div className="exam-detail-item">
              <span className="exam-detail-label">
                Maximum Marks:
              </span>{" "}
              {paper?.totalMarks || calculatedMarks}
            </div>

            <div className="exam-detail-item">
              <span className="exam-detail-label">
                Total Questions:
              </span>{" "}
              {paper?.sections?.reduce(
                (total, section) =>
                  total + (section?.questions?.length || 0),
                0
              )}
            </div>

          </div>

          {/* ===============================
              SECTIONS
          =============================== */}

          {paper?.sections?.map((section, sectionIndex) => (

            <div
              className="question-section"
              key={section?.id || sectionIndex}
            >

              {/* Section heading */}

              <div className="question-section-title">
                {section?.name || `Section ${sectionIndex + 1}`}
              </div>

              {/* Instructions */}

              {section?.instructions && (
                <div className="question-section-instructions">
                  <strong>Instructions:</strong>{" "}
                  {section.instructions}
                </div>
              )}

              {/* Questions */}

              {section?.questions?.map(
                (question, questionIndex) => {

                  questionNumber += 1;

                  return (
                    <div
                      className="question-row"
                      key={
                        question?.id ||
                        `${sectionIndex}-${questionIndex}`
                      }
                    >

                      {/* Question number */}

                      <div className="question-number">
                        {questionNumber}.
                      </div>

                      {/* Question content */}

                      <div
                        className="question-content"
                        dangerouslySetInnerHTML={{
                          __html: renderQuestion(
                            question?.content
                          ),
                        }}
                      />

                      {/* Marks */}

                      <div className="question-marks">
                        [
                        {question?.marks ?? 0}
                        ]
                      </div>

                    </div>
                  );
                }
              )}

            </div>

          ))}

          {/* ===============================
              FOOTER
          =============================== */}

          <div className="question-paper-footer">
            <div>
              End of Question Paper
            </div>
          </div>

        </div>

      </div>

    </div>
  );
};

export default QuestionPaperPrint;